import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { FileText, ArrowLeft } from "lucide-react";

function Navigation() {
  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <Link href="/" data-testid="link-home">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
        </Link>
        <nav className="hidden md:flex items-center gap-6">
          <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground">
            Features
          </Link>
          <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
            Pricing
          </Link>
          <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground">
            Docs
          </Link>
          <Link href="/blog" className="text-sm text-muted-foreground hover:text-foreground">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/login">
            <Button variant="ghost" size="sm">
              Login
            </Button>
          </Link>
          <Link href="/signup">
            <Button size="sm">
              Sign Up Free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="/logo.png" alt="ReviewKit" className="h-10 object-contain" />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            <Link href="/features" className="text-sm text-muted-foreground hover:text-foreground">
              Features
            </Link>
            <Link href="/pricing" className="text-sm text-muted-foreground hover:text-foreground">
              Pricing
            </Link>
            <Link href="/docs" className="text-sm text-muted-foreground hover:text-foreground">
              Docs
            </Link>
            <Link href="/contact" className="text-sm text-muted-foreground hover:text-foreground">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-muted-foreground hover:text-foreground">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-foreground font-medium">
              Terms
            </Link>
          </nav>
          <div className="text-sm text-muted-foreground">
            {new Date().getFullYear()} ReviewKit. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Link href="/">
            <Button variant="ghost" size="sm" className="gap-2 mb-4">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
              <FileText className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Terms of Service</h1>
              <p className="text-muted-foreground mt-1">Last Updated: December 23, 2025</p>
            </div>
          </div>
        </div>

        <article className="prose prose-neutral dark:prose-invert max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Agreement to Terms</h2>
            <p className="text-muted-foreground mb-4">
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") and <strong>Plumbumpy Properties LLC</strong>, a Colorado limited liability company doing business as <strong>ReviewKit</strong> ("Company," "we," "us," or "our").
            </p>
            <p className="text-muted-foreground">
              By accessing or using ReviewKit at reviewkit.io (the "Service"), you agree to be bound by these Terms. If you do not agree to these Terms, do not use the Service.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Description of Service</h2>
            <p className="text-muted-foreground mb-4">
              ReviewKit is a reputation management platform that helps local service businesses collect customer reviews through email requests and QR codes. The Service includes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Sending review request emails to your customers</li>
              <li>Generating QR codes that link to review collection pages</li>
              <li>Collecting customer feedback and routing positive reviews to Google</li>
              <li>Dashboard analytics and staff performance tracking</li>
              <li>Storage of business data, customer contact information, and interaction history</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Eligibility</h2>
            <p className="text-muted-foreground">
              You must be at least 18 years old and capable of forming a binding contract to use the Service. By using ReviewKit, you represent that you meet these requirements and have the authority to bind yourself or your organization to these Terms.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Account Registration</h2>
            <p className="text-muted-foreground mb-4">
              To use the Service, you must create an account by providing accurate and complete information. You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Acceptable Use</h2>
            <p className="text-muted-foreground mb-4">
              You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree NOT to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Send unsolicited or spam emails through the Service</li>
              <li>Use the Service to harass, abuse, or harm others</li>
              <li>Collect or store customer data without proper consent</li>
              <li>Impersonate any person or entity</li>
              <li>Violate any applicable laws, including anti-spam laws (CAN-SPAM, GDPR, CCPA)</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Use the Service to send emails to purchased or scraped email lists</li>
              <li>Misrepresent your relationship with your customers</li>
              <li>Use the Service in any way that could damage, disable, or impair the Service</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              <strong>Email Compliance:</strong> You are solely responsible for ensuring that all emails sent through ReviewKit comply with applicable laws, including obtaining proper consent from recipients. ReviewKit is not responsible for your compliance with anti-spam regulations.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Customer Data and Consent</h2>
            <p className="text-muted-foreground mb-4">You represent and warrant that:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You have obtained all necessary consents from your customers before entering their information into ReviewKit</li>
              <li>You have a legitimate business relationship with each person you send review requests to</li>
              <li>You will honor all opt-out and unsubscribe requests promptly</li>
              <li>You will not use the Service to contact individuals who have not done business with you</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Subscription Plans and Pricing</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">6.1 Available Plans</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border mb-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border px-4 py-2 text-left">Plan</th>
                    <th className="border border-border px-4 py-2 text-left">Price</th>
                    <th className="border border-border px-4 py-2 text-left">Request Limit</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr>
                    <td className="border border-border px-4 py-2">Free</td>
                    <td className="border border-border px-4 py-2">$0/month</td>
                    <td className="border border-border px-4 py-2">10 requests/month</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Lifetime</td>
                    <td className="border border-border px-4 py-2">$79 one-time</td>
                    <td className="border border-border px-4 py-2">500 requests/month</td>
                  </tr>
                  <tr>
                    <td className="border border-border px-4 py-2">Pro</td>
                    <td className="border border-border px-4 py-2">$29/month</td>
                    <td className="border border-border px-4 py-2">Unlimited requests</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-medium mt-6 mb-3">6.2 Billing</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Lifetime Plan:</strong> One-time payment grants permanent access subject to these Terms</li>
              <li><strong>Pro Plan:</strong> Billed monthly in advance. Your subscription will automatically renew unless canceled</li>
              <li><strong>Free Plan:</strong> No payment required; subject to usage limits</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">6.3 Refunds</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Lifetime Plan:</strong> 30-day money-back guarantee from date of purchase</li>
              <li><strong>Pro Plan:</strong> No refunds for partial months; you may cancel at any time and retain access until the end of your billing period</li>
              <li>Refund requests must be submitted to support@reviewkit.io</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">6.4 Price Changes</h3>
            <p className="text-muted-foreground">
              We reserve the right to change pricing at any time. Existing Lifetime plan holders will be grandfathered at their original terms. Pro subscribers will be notified at least 30 days before any price increase takes effect.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Intellectual Property</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">7.1 Our Property</h3>
            <p className="text-muted-foreground mb-4">
              The Service, including all software, designs, text, graphics, and other content, is owned by Plumbumpy Properties LLC and protected by intellectual property laws. You may not copy, modify, distribute, or reverse engineer any part of the Service.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">7.2 Your Content</h3>
            <p className="text-muted-foreground mb-4">
              You retain ownership of all data, logos, and content you upload to ReviewKit ("Your Content"). By uploading Your Content, you grant us a limited license to use, store, and display Your Content solely to provide the Service to you.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">7.3 Feedback</h3>
            <p className="text-muted-foreground">
              Any suggestions, ideas, or feedback you provide about the Service may be used by us without any obligation to compensate you.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Third-Party Services</h2>
            <p className="text-muted-foreground mb-4">The Service integrates with third-party services including:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Google Places API:</strong> For business search and review URL generation</li>
              <li><strong>Stripe:</strong> For payment processing</li>
              <li><strong>Resend:</strong> For email delivery</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Your use of these third-party services is subject to their respective terms and privacy policies. We are not responsible for the actions or policies of third-party providers.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground mb-4 uppercase font-medium">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>MERCHANTABILITY</li>
              <li>FITNESS FOR A PARTICULAR PURPOSE</li>
              <li>NON-INFRINGEMENT</li>
              <li>ACCURACY OR RELIABILITY OF RESULTS</li>
            </ul>
            <p className="text-muted-foreground mt-4">We do not guarantee that:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>The Service will be uninterrupted or error-free</li>
              <li>Emails sent through the Service will be delivered</li>
              <li>The Service will increase your Google reviews or business revenue</li>
              <li>Any specific results will be achieved through use of the Service</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Limitation of Liability</h2>
            <p className="text-muted-foreground mb-4 uppercase font-medium">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
            <p className="text-muted-foreground mb-4">
              <strong>10.1</strong> IN NO EVENT SHALL PLUMBUMPY PROPERTIES LLC, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.
            </p>
            <p className="text-muted-foreground mb-4">
              <strong>10.2</strong> OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
            </p>
            <p className="text-muted-foreground">
              <strong>10.3</strong> THESE LIMITATIONS APPLY REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE) AND EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Indemnification</h2>
            <p className="text-muted-foreground mb-4">
              You agree to indemnify, defend, and hold harmless Plumbumpy Properties LLC and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising from:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Emails you send through the Service</li>
              <li>Your failure to obtain proper consent from your customers</li>
              <li>Any claim that your use of the Service caused harm to a third party</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Termination</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">12.1 By You</h3>
            <p className="text-muted-foreground mb-4">
              You may cancel your account at any time by contacting support@reviewkit.io or through your account settings.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">12.2 By Us</h3>
            <p className="text-muted-foreground mb-4">We may suspend or terminate your account immediately, without prior notice, if:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>You violate these Terms</li>
              <li>You engage in fraudulent or illegal activity</li>
              <li>We are required to do so by law</li>
              <li>We discontinue the Service</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">12.3 Effect of Termination</h3>
            <p className="text-muted-foreground mb-4">Upon termination:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Your right to use the Service will immediately cease</li>
              <li>We may delete your account and all associated data after 30 days</li>
              <li>Provisions that by their nature should survive termination will survive (including Sections 9, 10, 11, and 14)</li>
            </ul>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Changes to Terms</h2>
            <p className="text-muted-foreground mb-4">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Posting the updated Terms on reviewkit.io</li>
              <li>Updating the "Last Updated" date</li>
              <li>Sending an email to your registered address for significant changes</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Governing Law and Dispute Resolution</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">14.1 Governing Law</h3>
            <p className="text-muted-foreground mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Colorado, without regard to its conflict of law provisions.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">14.2 Jurisdiction</h3>
            <p className="text-muted-foreground mb-4">
              Any legal action or proceeding arising from these Terms shall be brought exclusively in the state or federal courts located in Boulder County, Colorado. You consent to the personal jurisdiction of these courts.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">14.3 Informal Resolution</h3>
            <p className="text-muted-foreground">
              Before filing any legal claim, you agree to attempt to resolve the dispute informally by contacting us at legal@reviewkit.io. We will attempt to resolve the dispute within 30 days.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">15. General Provisions</h2>
            
            <h3 className="text-lg font-medium mt-6 mb-3">15.1 Entire Agreement</h3>
            <p className="text-muted-foreground mb-4">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Plumbumpy Properties LLC regarding the Service.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">15.2 Severability</h3>
            <p className="text-muted-foreground mb-4">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">15.3 Waiver</h3>
            <p className="text-muted-foreground mb-4">
              Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">15.4 Assignment</h3>
            <p className="text-muted-foreground mb-4">
              You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations without restriction.
            </p>

            <h3 className="text-lg font-medium mt-6 mb-3">15.5 No Agency</h3>
            <p className="text-muted-foreground">
              Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship between you and us.
            </p>
          </section>

          <hr className="my-8 border-border" />

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">16. Contact Information</h2>
            <p className="text-muted-foreground mb-4">If you have any questions about these Terms, please contact us:</p>
            <p className="text-muted-foreground">
              <strong>Plumbumpy Properties LLC dba ReviewKit</strong><br />
              Email: <a href="mailto:legal@reviewkit.io" className="text-primary hover:underline">legal@reviewkit.io</a><br />
              Support: <a href="mailto:support@reviewkit.io" className="text-primary hover:underline">support@reviewkit.io</a><br />
              Website: <a href="https://reviewkit.io" className="text-primary hover:underline">https://reviewkit.io</a>
            </p>
          </section>

          <hr className="my-8 border-border" />

          <p className="text-muted-foreground italic text-center">
            By using ReviewKit, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}


